import { useEffect, useState } from 'react';
import { archiveApi } from './client';

/**
 * The rendered page as an object URL.
 *
 * The image route is authenticated like every other one, and an `<img src>`
 * cannot carry a bearer token — so the bytes are fetched and wrapped instead of
 * linked. The URL is revoked when the page changes or the component unmounts;
 * without that, stepping through a binder leaks one blob per page for as long as
 * the tab is open.
 *
 * Returns null while loading and on failure. A missing render is an ordinary
 * state here — most of the corpus has one, some pages do not — so the caller
 * shows its placeholder rather than an error.
 */
export function usePageImage(pdfId: number | undefined, page: number | undefined) {
  const key = pdfId === undefined || page === undefined ? null : `${pdfId}:${page}`;
  const [loaded, setLoaded] = useState<{ key: string; url: string } | null>(null);

  useEffect(() => {
    if (pdfId === undefined || page === undefined || key === null) return;

    const abort = new AbortController();
    let objectUrl: string | null = null;

    archiveApi
      .pageImage(pdfId, page, abort.signal)
      .then((next) => {
        objectUrl = next;
        setLoaded({ key, url: next });
      })
      .catch(() => undefined);

    return () => {
      abort.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [key, page, pdfId]);

  return key !== null && loaded?.key === key ? loaded.url : null;
}
